import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BillingFields, emptyBilling, billingValid } from "@/components/BillingFields";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CheckCircle2, Lock } from "lucide-react";

const fmt = (n) => Number(n).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function PlanSummary({ plan }) {
  if (!plan) return null;
  return (
    <div className="bg-emerald-900 text-white rounded-3xl p-7 md:p-8" data-testid="plan-summary">
      <p className="text-emerald-300 text-sm font-medium">LAIzyDocu Pro</p>
      <div className="mt-3 flex items-end gap-2">
        <span className="font-display text-4xl font-semibold">{fmt(plan.monthly_gross)} €</span>
        <span className="text-emerald-200 mb-1">/ Monat (inkl. MwSt.)</span>
      </div>
      <ul className="mt-6 space-y-2.5 text-sm text-emerald-50">
        {["DSGVO-konforme Einwilligungen & Behandlungsverträge","Patienten-Kiosk per QR-Code","Digitale Signatur & PDF-Archiv","Mandantenfähig & verschlüsselt"].map((f) => (
          <li key={f} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-300 shrink-0" />{f}</li>
        ))}
      </ul>
      <div className="mt-6 pt-5 border-t border-emerald-700/60 text-sm space-y-1.5">
        <div className="flex justify-between"><span className="text-emerald-200">Einmalige Einrichtung</span><span>{fmt(plan.setup_net)} € netto</span></div>
        <div className="flex justify-between"><span className="text-emerald-200">1. Monat</span><span>{fmt(plan.monthly_gross)} €</span></div>
        <div className="flex justify-between font-semibold text-base mt-2"><span>Heute fällig</span><span data-testid="first-payment-amount">{fmt(plan.first_payment)} €</span></div>
        <p className="text-xs text-emerald-300/80 pt-1">Danach automatisch {fmt(plan.monthly_gross)} € / Monat. Jederzeit kündbar.</p>
      </div>
    </div>
  );
}

export default function DirectRegister() {
  const [plan, setPlan] = useState(null);
  const [practiceName, setPracticeName] = useState("");
  const [billing, setBilling] = useState(emptyBilling);
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/payments/plan").then(({ data }) => setPlan(data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (practiceName.trim().length < 2) return toast.error("Bitte Praxisnamen angeben.");
    if (!billingValid(billing)) return toast.error("Bitte alle Rechnungsfelder vollständig ausfüllen.");
    if (pw.length < 8) return toast.error("Passwort muss mind. 8 Zeichen haben.");
    if (pw !== pw2) return toast.error("Passwörter stimmen nicht überein.");
    setLoading(true);
    try {
      const { data } = await api.post("/payments/register-checkout", {
        practice_name: practiceName.trim(), email: billing.email.trim().toLowerCase(),
        password: pw, billing_address: { ...billing, email: billing.email.trim().toLowerCase() },
      });
      localStorage.setItem("pending_payment_id", data.payment_id);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registrierung fehlgeschlagen.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="direct-register-page">
      <SiteNav />
      <section className="max-w-5xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-20">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-sm font-medium">
          <ShieldCheck className="w-4 h-4" /> Jetzt buchen
        </span>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold text-emerald-900 leading-[1.05] mt-6 mb-3">Praxis registrieren & loslegen</h1>
        <p className="text-stone-600 max-w-xl mb-10">Erstellen Sie Ihr Konto und schließen Sie die Buchung sicher über Mollie ab. Direkt nach der Zahlung ist Ihr Zugang vollständig freigeschaltet.</p>

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-8 items-start">
          <form onSubmit={submit} className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-7 md:p-9 space-y-6">
            <div className="space-y-1.5">
              <Label htmlFor="practice-name" className="text-sm text-stone-600">Praxisname</Label>
              <Input id="practice-name" value={practiceName} onChange={(e) => setPracticeName(e.target.value)}
                data-testid="reg-practice-name" placeholder="z. B. Physiopraxis Muster" className="h-12 rounded-xl" />
            </div>
            <div className="pt-2 border-t border-stone-100">
              <p className="text-sm font-medium text-stone-700 mb-4 mt-4">Rechnungsadresse</p>
              <BillingFields value={billing} onChange={setBilling} />
            </div>
            <div className="pt-2 border-t border-stone-100">
              <p className="text-sm font-medium text-stone-700 mb-4 mt-4">Passwort für Ihr Konto</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="pw" className="text-sm text-stone-600">Passwort</Label>
                  <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reg-password" className="h-12 rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pw2" className="text-sm text-stone-600">Passwort wiederholen</Label>
                  <Input id="pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reg-password2" className="h-12 rounded-xl" />
                </div>
              </div>
              <p className="text-xs text-stone-400 mt-2">Mind. 8 Zeichen, inkl. Groß-/Kleinbuchstabe, Zahl und Sonderzeichen.</p>
            </div>
            <Button type="submit" disabled={loading} data-testid="reg-submit"
              className="w-full h-14 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white text-base font-medium">
              {loading ? <><Loader2 className="mr-2 w-5 h-5 animate-spin" /> Weiterleitung zur Zahlung…</> : <><Lock className="mr-2 w-4 h-4" /> Zahlungspflichtig buchen</>}
            </Button>
            <p className="text-xs text-stone-400 text-center">
              Mit der Buchung akzeptieren Sie die <Link to="/agb" className="underline">AGB</Link> und <Link to="/datenschutz" className="underline">Datenschutzerklärung</Link>.
            </p>
          </form>
          <div className="lg:sticky lg:top-24"><PlanSummary plan={plan} /></div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
